import { Routes } from '@angular/router';
import { canActivate, redirectLoggedInTo } from '@angular/fire/auth-guard';
import { mainRoutes } from './main-layout/main-routes';
import { passwordChangeGuard } from './guards/password-change.guard';
import { storeSelectedGuard } from './guards/store-selected.guard';
import { storeExpiredGuard } from './guards/store-expired.guard';
import { permissionGuard } from './guards/permission.guard';
import { authGuard } from './guards/auth.guard';
import { adminRedirectGuard } from './guards/admin-redirect.guard';
import { FeatureFlagGuard } from './guards/feature-flag.guard';

const redirectLoggedInToItems = () => redirectLoggedInTo(['/store']);

export const routes: Routes = [
   {
      path: 'access-denied',
      loadComponent: () =>
         import('./access-denied/access-denied.component').then(
            (m) => m.AccessDeniedComponent,
         ),
      title: 'Access Denied | KOTTRA POS',
   },
   {
      path: 'privacy',
      loadComponent: () =>
         import('./privacy/privacy-policy.component').then(
            (m) => m.PrivacyPolicyComponent,
         ),
      title: 'Privacy Policy | KOTTRA POS',
   },
   {
      path: 'terms',
      loadComponent: () =>
         import('./terms/terms-of-service.component').then(
            (m) => m.TermsOfServiceComponent,
         ),
      title: 'Terms of Service | KOTTRA POS',
   },
   {
      path: 'guide',
      redirectTo: 'guide/cashier',
      pathMatch: 'full',
   },
   {
      path: 'guide/:role',
      loadComponent: () => import('./guide/guide').then((m) => m.Guide),
      title: 'Training Guide | KOTTRA POS',
   },
   {
      path: 'login',
      loadComponent: () =>
         import('./auth/login/login.component').then((m) => m.LoginComponent),
      ...canActivate(redirectLoggedInToItems),
      title: 'Login',
   },
   {
      path: 'register',
      loadComponent: () =>
         import('./auth/register/register.component').then(
            (m) => m.RegisterComponent,
         ),
      ...canActivate(redirectLoggedInToItems),
      title: 'Register',
   },
   {
      path: 'forgot-password',
      loadComponent: () =>
         import('./auth/forgot-password/forgot-password.component').then(
            (m) => m.ForgotPasswordComponent,
         ),
      ...canActivate(redirectLoggedInToItems),
      title: 'Forgot Password',
   },
   {
      path: 'change-password',
      loadComponent: () =>
         import('./auth/change-password/change-password.component').then(
            (m) => m.ChangePasswordComponent,
         ),
      canActivate: [authGuard],
      title: 'Change Password',
   },

   {
      path: 'store',
      loadComponent: () =>
         import('./store/store.component').then((m) => m.StoreComponent),
      canActivate: [authGuard, passwordChangeGuard],
      data: { title: 'Store', titleKH: 'ហាង' },
   },
   {
      path: 'cashier',
      loadComponent: () =>
         import('./cashier/dashboard/dashboard.component').then(
            (m) => m.DashboardComponent,
         ),
      canActivate: [
         authGuard,
         passwordChangeGuard,
         storeSelectedGuard,
         storeExpiredGuard,
         permissionGuard('view_sales'),
         FeatureFlagGuard,
      ],
      data: {
         title: 'Dashboard - Cashier',
         titleKH: 'ផ្ទាំងគ្រប់គ្រង - អ្នកគិតលុយ',
         preload: true,
         featureKey: 'core_pos',
      },
   },
   {
      path: 'sell',
      loadComponent: () =>
         import('./cashier/sell/sell.component').then((m) => m.SellComponent),
      canActivate: [authGuard, passwordChangeGuard, storeSelectedGuard, storeExpiredGuard],
      data: { title: 'Sell', titleKH: 'ផ្ទាំងលក់', preload: true },
   },
   {
      path: 'captain-order',
      loadComponent: () =>
         import('./cashier/sell/sell.component').then((m) => m.SellComponent),
      canActivate: [
         authGuard,
         passwordChangeGuard,
         storeSelectedGuard,
         storeExpiredGuard,
         permissionGuard('create_captain_order'),
         FeatureFlagGuard,
      ],
      data: {
         title: 'Captain Order',
         titleKH: 'កម្ម៉ង់ដោយប្រធាន',
         isCaptainOrder: true,
         featureKey: 'captain_order',
         preload: true,
      },
   },
   {
      // Old URL (was "Order History") — keep bookmarks and PWA shortcuts working.
      path: 'order-history',
      redirectTo: 'action-history',
      pathMatch: 'full',
   },
   {
      path: 'action-history',
      loadComponent: () =>
         import('./cashier/action-history/action-history.component').then(
            (m) => m.ActionHistoryComponent,
         ),
      canActivate: [
         authGuard,
         passwordChangeGuard,
         storeSelectedGuard,
         storeExpiredGuard,
         permissionGuard('view_order_history'),
      ],
      data: {
         title: 'Action History',
         titleKH: 'ប្រវត្តិនៃសកម្មភាព',
         sourceFilter: ['Sell', 'Captain Order', 'Cash Movement'],
         defaultToOpenShift: true,
      },
   },
   {
      path: 'trash',
      loadComponent: () =>
         import('./cashier/action-history/action-history.component').then(
            (m) => m.ActionHistoryComponent,
         ),
      canActivate: [
         authGuard,
         passwordChangeGuard,
         storeSelectedGuard,
         storeExpiredGuard,
         permissionGuard('view_order_history'),
      ],
      data: {
         title: 'Trash',
         titleKH: 'ធុងសំរាម',
         sourceFilter: ['Sell', 'Captain Order'],
         showOnlyVoid: true,
      },
   },
   {
      path: 'kot-display',
      loadComponent: () =>
         import('./cashier/kot-display/kot-display.component').then(
            (m) => m.KotDisplayComponent,
         ),
      canActivate: [
         authGuard,
         passwordChangeGuard,
         storeSelectedGuard,
         storeExpiredGuard,
         permissionGuard('view_kot_display'),
         FeatureFlagGuard,
      ],
      data: {
         title: 'Kitchen Display',
         titleKH: 'ផ្ទាំងបង្ហាញផ្ទះបាយ',
         preload: true,
         featureKey: 'kot_display',
      },
   },
   {
      path: 'customer-display',
      loadComponent: () =>
         import('./cashier/customer-display/customer-display.component').then(
            (m) => m.CustomerDisplayComponent,
         ),
      canActivate: [
         authGuard,
         passwordChangeGuard,
         storeSelectedGuard,
         storeExpiredGuard,
         FeatureFlagGuard,
      ],
      data: {
         title: 'Customer Display',
         titleKH: 'ផ្ទាំងបង្ហាញអតិថិជន',
         preload: true,
         featureKey: 'customer_display',
      },
   },

   {
      // Super-admin moved to the kottra-admin app — forward any /admin URL there.
      path: 'admin',
      children: [
         { path: '**', canActivate: [adminRedirectGuard], children: [] },
      ],
   },
   {
      path: '',
      loadComponent: () =>
         import('./main-layout/main-layout.component').then(
            (m) => m.MainLayoutComponent,
         ),
      canActivate: [authGuard, passwordChangeGuard, storeSelectedGuard],
      canActivateChild: [storeExpiredGuard],
      children: mainRoutes,
   },
   {
      path: '404',
      loadComponent: () =>
         import('./not-found/not-found.component').then(
            (m) => m.NotFoundComponent,
         ),
      title: 'Page Not Found | KOTTRA POS',
   },
   {
      path: '**',
      loadComponent: () =>
         import('./not-found/not-found.component').then(
            (m) => m.NotFoundComponent,
         ),
      title: 'Page Not Found | KOTTRA POS',
   },
];
